import { useApi } from '../api';
import { Card, ErrorBanner, Loading, StatTile } from '../components/ui';
import { useT, type Clave } from '../i18n';

type ServiceLink = {
  label: string;
  url: string;
  kind: 'dashboard' | 'keys' | 'docs' | 'usage';
};

type ExternalService = {
  id: string;
  name: string;
  role: string;
  status: 'configured' | 'missing' | 'phase_3' | 'built_in';
  envKeys: string[];
  cost: string;
  links: ServiceLink[];
};

type ServicesData = {
  services: ExternalService[];
  summary: { configured: number; missing: number; pending: number };
  textProviders: { name: string; available: boolean }[];
  zeroSpendMode: boolean;
};

const STATUS_STYLE: Record<ExternalService['status'], string> = {
  configured: 'bg-[var(--color-ok)]/15 text-[var(--color-ok)]',
  missing: 'bg-[var(--color-warn)]/15 text-[var(--color-warn)]',
  phase_3: 'bg-[var(--color-ink-muted)]/15 text-[var(--color-ink-muted)]',
  built_in: 'bg-[var(--color-accent)]/15 text-[var(--color-accent)]',
};

const LINK_STYLE: Record<ServiceLink['kind'], string> = {
  dashboard: 'bg-[var(--color-accent)] text-white',
  keys: 'border border-[var(--color-accent)] text-[var(--color-accent)]',
  usage: 'border border-[var(--color-border-subtle)]',
  docs: 'border border-[var(--color-border-subtle)] text-[var(--color-ink-muted)]',
};

/**
 * Servicios externos.
 *
 * Reúne en un lugar las consolas de todo lo que este proyecto toca, pero no es una lista
 * de favoritos: cada servicio muestra **qué hace acá dentro**, qué variables lo activan y
 * si está configurado ahora mismo. Eso es lo que no se ve entrando a cada consola por
 * separado — cuáles de los nueve ya están conectados y cuáles siguen pendientes.
 *
 * El estado lo calcula el servidor, que es el único que ve las variables de entorno, y
 * **nunca viaja el valor de una clave**: sólo si está presente.
 */
export function Services() {
  const { t } = useT();
  const state = useApi<ServicesData>('/api/services', 60_000);
  if (state.loading && !state.data) return <Loading label={t('servicios.cargando')} />;
  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;
  if (!state.data) return null;

  const data = state.data;
  const grouped = {
    activos: data.services.filter((s) => s.status === 'configured' || s.status === 'built_in'),
    pendientes: data.services.filter((s) => s.status === 'missing'),
    futuros: data.services.filter((s) => s.status === 'phase_3'),
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">{t('servicios.titulo')}</h1>
        <p className="mt-1 text-sm text-[var(--color-ink-muted)]">{t('servicios.subtitulo')}</p>
      </div>

      {/* Dos columnas ya en el teléfono: cifras cortas, y en una sola cada una ocupaba media pantalla. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        <StatTile label={t('servicios.activos')} value={grouped.activos.length} tone="ok" />
        <StatTile
          label={t('servicios.sinConfigurar')}
          value={grouped.pendientes.length}
          tone={grouped.pendientes.length > 0 ? 'warn' : 'neutral'}
          hint={grouped.pendientes.length > 0 ? t('servicios.sumanCapacidad') : undefined}
        />
        <StatTile label={t('servicios.fase3')} value={grouped.futuros.length} />
      </div>

      {(
        [
          ['servicios.activos', grouped.activos],
          ['servicios.sinConfigurar', grouped.pendientes],
          ['servicios.previstos', grouped.futuros],
        ] as [Clave, ExternalService[]][]
      ).map(([title, services]) =>
        services.length === 0 ? null : (
          <section key={title}>
            <h2 className="mb-3 text-sm font-semibold tracking-wide uppercase text-[var(--color-ink-muted)]">
              {t(title)}
            </h2>
            {/*
            `items-start`: cada tarjeta mide lo que necesita.

            Sin esto, la rejilla reparte a sus hijos con `stretch` y la tarjeta más corta de cada
            fila se estira hasta el alto de su vecina. Medido: «Influencers» quedaba con 538 px de
            vacío debajo de la última fila porque al lado había tres tarjetas apiladas. No es un
            problema de relleno ni de márgenes —el borde estaba donde la rejilla dijo—, y por eso
            se lee como que el diseño está corrido.

            El precio es que las columnas terminan disparejas. Es lo correcto: dicen dónde termina
            cada cosa, en vez de dibujar un marco alrededor de nada.
            */}
            <div className="grid items-start gap-4 lg:grid-cols-2">
              {services.map((service) => (
                <Card key={service.id}>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-base font-semibold">{service.name}</h3>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLE[service.status]}`}
                    >
                      {t(`servicios.estado.${service.status}` as Clave)}
                    </span>
                  </div>

                  {/*
                    Un párrafo por bloque separado con línea en blanco. Antes todo el texto
                    caía en un solo `<p>`, así que una descripción con condiciones y
                    requisitos se leía como un muro y nadie llegaba al final.
                  */}
                  <div className="mt-2 space-y-2 text-sm text-[var(--color-ink-muted)]">
                    {service.role.split('\n\n').map((paragraph) => (
                      <p key={paragraph.slice(0, 32)}>{paragraph}</p>
                    ))}
                  </div>

                  <p className="mt-3 text-xs">
                    <span className="text-[var(--color-ink-muted)]">{t('servicios.costo')}</span>
                    {service.cost}
                  </p>

                  {service.envKeys.length > 0 && (
                    <p className="mt-1 font-mono text-xs text-[var(--color-ink-muted)]">
                      {service.envKeys.join(' · ')}
                    </p>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    {service.links.map((link) => (
                      <a
                        key={link.url}
                        href={link.url}
                        target="_blank"
                        // `noopener` evita que la pestaña abierta pueda manipular esta.
                        rel="noreferrer noopener"
                        // `min-h-11` en pantallas táctiles: son enlaces con forma de botón, y medían 28 px.
                        // La regla global de `styles.css` cubre a `button`, no a `a` — un enlace
                        // puede estar en medio de una frase y estirarlo rompería el renglón.
                        className={`inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-medium transition hover:opacity-90 [@media(pointer:coarse)]:min-h-11 ${LINK_STYLE[link.kind]}`}
                      >
                        {link.label} ↗
                      </a>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </section>
        ),
      )}

      <Card title={t('servicios.comoSeCargan')}>
        <p className="text-sm">{t('servicios.dondeVan')}</p>
        <pre className="mt-3 overflow-x-auto rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-3 text-xs">
          {'make cf-secrets\npnpm --filter @vcos/worker exec wrangler secret put GEMINI_API_KEY'}
        </pre>
        <p className="mt-3 text-xs text-[var(--color-ink-muted)]">{t('servicios.nuncaMuestra')}</p>
      </Card>
    </div>
  );
}
